// /scada/dispatch — fleet overview (R1, full). Dense one-row-per-station view
// with fleet KPIs, region filter and sorting.
//
// Пошук станції прибрано: конкретну СЕС обирають селектором у топбарі.
// Фільтр за станом прибрано теж — сторінка свідомо показує весь парк,
// а стан кожної станції видно колонкою «Стан».
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { SectionCard, StatusBadge, QualityDot, MetricTile } from '@/components/scada/kit';
import { scada, type OpState } from '@/scada/tokens';
import { STATIONS } from '@/scada/mock/stations';
import { useFleetTelemetry } from '@/scada/useTelemetry';
import { useAllowedStationIds } from '@/scada/useScopedStations';

const cfgOf = (id: string) => STATIONS.find((s) => s.id === id)!;
const nameOf = (id: string) => cfgOf(id)?.name ?? id;
const fmtKw = (kw: number) => (kw >= 1000 ? `${(kw / 1000).toFixed(2)} МВт` : `${Math.round(kw)} кВт`);

const LIMIT_LABEL: Record<string, string> = {
  sko: 'СКО', operational_safety: 'Опер. безпека', technical: 'Тех. ліміт', manual: 'Ручний',
};
const MODE_LABEL: Record<string, string> = {
  manual: 'Ручний', auto: 'Авто', local: 'Локальний', remote: 'Дистанц.', blocked: '—',
};

type SortKey = 'power' | 'name' | 'state';
const STATE_ORDER: OpState[] = ['alarm', 'offline', 'warning', 'curtailed', 'commissioning', 'ok'];

export default function ScadaDispatchPage() {
  const { data: rawData, updatedAt } = useFleetTelemetry();
  const allowed = useAllowedStationIds();
  const data = useMemo(() => (allowed ? rawData.filter((d) => allowed.has(d.stationId)) : rawData), [rawData, allowed]);
  const stations = useMemo(() => (allowed ? STATIONS.filter((s) => allowed.has(s.id)) : STATIONS), [allowed]);
  const [region, setRegion] = useState('all');
  const [sort, setSort] = useState<SortKey>('power');

  const regions = useMemo(() => Array.from(new Set(stations.map((s) => s.region))), [stations]);

  const totalP = data.reduce((s, d) => s + d.activePowerKw, 0);
  const totalCap = stations.reduce((s, c) => s + c.acCapacityKw, 0);
  const totalDaily = data.reduce((s, d) => s + d.dailyEnergyKwh, 0);
  const totalAvail = data.reduce((s, d) => s + d.availablePowerKw, 0);
  const offline = data.filter((d) => d.state === 'offline').length;
  const curtailed = data.filter((d) => d.limitReason).length;
  const warnings = data.reduce((s, d) => s + d.warnings + d.alarms, 0);

  const rows = useMemo(() => {
    let r = [...data];
    if (region !== 'all') r = r.filter((d) => cfgOf(d.stationId).region === region);
    r.sort((a, b) => {
      if (sort === 'name') return nameOf(a.stationId).localeCompare(nameOf(b.stationId));
      if (sort === 'state') return STATE_ORDER.indexOf(a.state) - STATE_ORDER.indexOf(b.state);
      return b.activePowerKw - a.activePowerKw;
    });
    return r;
  }, [data, region, sort]);

  const selStyle = { background: scada.bg, border: `1px solid ${scada.border}`, color: scada.text, borderRadius: 8, padding: '6px 10px', fontSize: 13 } as const;

  // KPI парку. Раніше цей блок стояв ПІД рядком переходів; тепер він іде
  // над ним — спершу стан портфеля, далі навігація.
  const fleetKpis = (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" style={{ marginBottom: 16 }}>
      <MetricTile label="Сумарна потужність" value={(totalP / 1000).toFixed(2)} unit="МВт" accent={scada.state.ok} />
      <MetricTile label="Встановлена" value={(totalCap / 1000).toFixed(1)} unit="МВт" />
      <MetricTile label="Доступний резерв" value={(Math.max(0, totalAvail - totalP) / 1000).toFixed(2)} unit="МВт" />
      <MetricTile label="Генерація сьогодні" value={(totalDaily / 1000).toFixed(1)} unit="МВт·год" />
      <MetricTile label="Обмеження / аварії" value={`${curtailed} / ${offline + warnings}`} accent={offline || warnings ? scada.state.alarm : curtailed ? scada.state.curtailed : undefined} />
    </div>
  );

  // Каркас (ідентифікація станції + підменю) належить ScadaLayout. Тут лише
  // контент сторінки: KPI парку і таблиця станцій.
  return (
    <>
      {fleetKpis}
      {/* Filter bar */}
      <div className="flex items-center gap-2 flex-wrap" style={{ marginBottom: 12 }}>
        <select value={region} onChange={(e) => setRegion(e.target.value)} style={selStyle}>
          <option value="all">Усі області</option>
          {regions.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <div className="flex items-center gap-1" style={{ marginLeft: 'auto' }}>
          <span style={{ color: scada.textMuted, fontSize: 12 }}>Сортувати:</span>
          <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)} style={selStyle}>
            <option value="power">за потужністю</option>
            <option value="name">за назвою</option>
            <option value="state">за станом</option>
          </select>
        </div>
      </div>

      <SectionCard
        title="Станції"
        subtitle={`Портфель ТОВ ТЕПЛА · показано ${rows.length} з ${data.length}`}
        right={<span style={{ color: scada.textMuted, fontSize: 11 }}>оновлено {updatedAt.toLocaleTimeString('uk-UA')}</span>}
        padded={false}
      >
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 960 }}>
            <thead>
              <tr style={{ background: scada.panel2 }}>
                {['Станція', 'Стан', 'P факт', 'Ліміт', 'Доступна', 'Інвертори', 'УЗЕ', 'Режим', 'Звʼязок', 'Якість', ''].map((h) => (
                  <th key={h} style={{ textAlign: 'left', color: scada.textMuted, fontSize: 11, fontWeight: 600, padding: '8px 12px', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => {
                const cap = cfgOf(d.stationId).acCapacityKw;
                const loadPct = cap ? Math.round((d.activePowerKw / cap) * 100) : 0;
                return (
                  <tr key={d.stationId} style={{ borderTop: `1px solid ${scada.border}` }}>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ color: scada.text, fontSize: 13, fontWeight: 600 }}>{nameOf(d.stationId)}</div>
                      <div style={{ color: scada.textMuted, fontSize: 10 }}>{cfgOf(d.stationId).region} · {(cap / 1000).toFixed(2)} МВт</div>
                    </td>
                    <td style={{ padding: '10px 12px' }}><StatusBadge state={d.state} small /></td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ color: scada.text, fontSize: 13, fontWeight: 600 }}>{fmtKw(d.activePowerKw)}</div>
                      <div style={{ height: 3, background: scada.panel2, borderRadius: 2, marginTop: 4, width: 80 }}>
                        <div style={{ height: 3, width: `${loadPct}%`, background: scada.state.ok, borderRadius: 2 }} />
                      </div>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      {d.activeLimitKw != null ? (
                        <span style={{ color: scada.state.curtailed, fontSize: 12 }}>
                          {fmtKw(d.activeLimitKw)}<br />
                          <span style={{ fontSize: 10, color: scada.textMuted }}>{d.limitReason ? LIMIT_LABEL[d.limitReason] : ''}</span>
                        </span>
                      ) : <span style={{ color: scada.textMuted, fontSize: 12 }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 12px', color: scada.textDim, fontSize: 12 }}>{fmtKw(d.availablePowerKw)}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ color: d.invertersWorking < d.inverterCount ? scada.state.warning : scada.textDim, fontSize: 12 }}>
                        {d.invertersWorking}/{d.inverterCount}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 12 }}>
                      {d.bess ? (
                        <span style={{ color: scada.textDim }}>SOC {d.bess.socPct ? `${d.bess.socPct}%` : '—'}<br />
                          <span style={{ fontSize: 10, color: d.bess.mode === 'charging' ? scada.state.curtailed : d.bess.mode === 'discharging' ? scada.state.ok : scada.textMuted }}>
                            {d.bess.mode === 'charging' ? 'заряд' : d.bess.mode === 'discharging' ? 'розряд' : 'очікує'}
                          </span>
                        </span>
                      ) : <span style={{ color: scada.textMuted }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 12px', color: scada.textDim, fontSize: 12 }}>{MODE_LABEL[d.controlMode] ?? d.controlMode}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span title="Хмара / EMS BOX / захищений канал" className="inline-flex gap-1">
                        <i className="ri-cloud-line" style={{ color: d.comms.cloudOnline ? scada.state.ok : scada.state.offline, fontSize: 14 }} />
                        <i className="ri-router-line" style={{ color: d.comms.emsBoxOnline ? scada.state.ok : scada.state.alarm, fontSize: 14 }} />
                        <i className="ri-shield-keyhole-line" style={{ color: d.comms.vpnOnline ? scada.state.ok : scada.state.offline, fontSize: 14 }} />
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px' }}><QualityDot quality={d.quality} label /></td>
                    <td style={{ padding: '10px 12px' }}>
                      <Link to={`/scada/station/${d.stationId}`}
                        className="inline-flex items-center gap-1 rounded-lg transition-colors"
                        style={{ color: scada.textDim, fontSize: 12, textDecoration: 'none', whiteSpace: 'nowrap', background: scada.panel, border: `1px solid ${scada.border}`, padding: '5px 10px' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = scada.panelHover; e.currentTarget.style.color = scada.text; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = scada.panel; e.currentTarget.style.color = scada.textDim; }}>
                        Деталі <i className="ri-arrow-right-s-line" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={11} style={{ padding: 24, textAlign: 'center', color: scada.textMuted, fontSize: 13 }}>Немає станцій за обраними фільтрами</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </>
  );
}
